import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import ChevronDownIcon from 'components/icons/ChevronDown';
import { colors } from 'utils/tailwindColors';

const Container = styled.div`
  ${({ isMobile }) => (isMobile ? 'width: 100%;' : 'width: 100%;')}
`;
const ListContainer = styled.div`
  ${({ isMobile }) => (isMobile ? 'max-height: 25rem;' : 'max-height: 27rem;')}
`;
const ProvinceList = styled.div`
  display: inline-grid;
  column-gap: 5rem;
`;

const Button = styled.button``;

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`;
const DropdownTrigger = styled.button`
  ${tw`font-graphie w-full flex items-center justify-between`}
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  background-color: ${colors.white};
  line-height: 1.75rem;
  height: 3.125rem;
  text-align: left;
  outline: none;
  ${({ active }) =>
    active
      ? `border: 2px solid ${colors['mattamy-blue']};`
      : `border: 1px solid ${colors['border-light-blue']};`}

  color: ${colors['mattamy-blue']};

  &:focus {
    border: 2px solid ${colors['mattamy-blue']};
  }
`;
const DropdownLabel = styled.span`
  ${tw`truncate`}
`;
const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 0.125rem;
  background-color: ${colors.white};
  border-radius: 0 0 0.5rem 0.5rem;
  border-color: rgba(179, 195, 205, 0.9);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  z-index: 20;
  overflow: hidden;
`;
const DropdownMenuTopBar = styled.div`
  height: 1.75rem;
  background-color: #d3d3d3;
`;
const IconContainer = styled.div`
  ${tw`flex items-center`}
  pointer-events: none;
  margin-left: 0.5rem;

  svg {
    color: ${colors['mattamy-blue']};
    transform: ${({ active }) => (active ? 'rotate(180deg)' : 'none')};
    transition: transform 0.15s ease;
  }
`;

const capitalizeWord = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};
const capitalizeProvince = (province = '') => {
  if (province.includes(' '))
    return province
      .split(' ')
      .map((word) => capitalizeWord(word))
      .join(' ');
  if (province.includes('-'))
    return province
      .split('-')
      .map((word) => capitalizeWord(word))
      .join('-');
  return capitalizeWord(province);
};

const Dropdown = ({
  label,
  active,
  setActive,
  dropdownName,
  dropdownDescription,
  children,
}) => {
  const hasLabel = Boolean(label);
  const displayLabel = hasLabel ? (
    label
  ) : (
    <span className='text-mattamy-blue/60'>
      {dropdownDescription || dropdownName}
    </span>
  );

  return (
    <DropdownWrapper>
      <DropdownTrigger
        type='button'
        onClick={() => setActive(!active)}
        aria-haspopup='listbox'
        aria-expanded={active}
        aria-label={dropdownName}
        active={active}
      >
        <DropdownLabel>{displayLabel}</DropdownLabel>
        <IconContainer active={active}>
          <ChevronDownIcon className='w-4 h-4' />
        </IconContainer>
      </DropdownTrigger>

      {active && (
        <DropdownMenu
          role='listbox'
          aria-label={dropdownDescription || dropdownName}
        >
          <DropdownMenuTopBar />
          {children}
        </DropdownMenu>
      )}
    </DropdownWrapper>
  );
};

const ProvincesTaxRateDropdown = (props) => {
  const {
    onProvinceChange,
    provincesList = [],
    active: activeProp,
    setActive: setActiveProp,
    provinceSelected = '',
  } = props;

  const ref = useRef();
  const [activeState, setActiveState] = useState(false);

  const active = activeProp ?? activeState;
  const setActive = setActiveProp ?? setActiveState;

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const provincesTaxesList = [...provincesList].sort((a, b) =>
    a.province.toLowerCase() > b.province.toLowerCase() ? 1 : -1,
  );

  return (
    <Container
      isMobile={isMobile}
      ref={ref}
      {...(!isMobile && {
        onBlur: () => {
          setTimeout(() => {
            if (ref && ref.current) {
              const containsFocusedElement = ref.current.contains(
                document.activeElement,
              );
              if (!containsFocusedElement) {
                setActive(false);
              }
            }
          }, 100);
        },
      })}
    >
      <Dropdown
        label={
          provincesTaxesList.length > 0 &&
          (provinceSelected ? (
            <span className='ml-0'>{capitalizeProvince(provinceSelected)}</span>
          ) : null)
        }
        active={active}
        setActive={setActive}
        dropdownName=''
        dropdownDescription=''
      >
        <ListContainer
          className='overflow-y-auto w-full'
          isMobile={isMobile}
          aria-live='polite'
        >
          <ProvinceList className='w-full'>
            {provincesTaxesList.map((province, index) => (
              <Button
                className='w-full px-4 py-1 relative flex items-center text-mattamy-blue  hover:bg-action-blue  hover:text-white'
                key={province.province + ' ' + index}
                onClick={() => {
                  onProvinceChange(province);
                  setActive(false);
                }}
                aria-label={province.province + ' province'}
              >
                <span className='font-graphie tracking-normal text-sm whitespace-nowrap'>
                  {capitalizeProvince(province.province)}
                </span>
              </Button>
            ))}
          </ProvinceList>
        </ListContainer>
      </Dropdown>
    </Container>
  );
};

export default ProvincesTaxRateDropdown;
